import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import refreshIcon from '@mui/icons-material/RefreshOutlined';
import { HttpStatusCode, isAxiosError } from 'axios';
import { RECEIPT } from 'common/constants/receipt';
import { Fragment, useCallback, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { saveLocalStorageData } from 'utils/saveLocalStorage';
import { useStoreSelector } from 'v3/data/store';
import {
  setContractSignDate,
  setContractingKey,
  setContractingSelected,
  setContractingState,
  setTimeRemaining,
  setTokenValid,
} from 'v3/data/store/contracting/actions';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { ContractingStateMapping } from 'v3/data/store/contracting/mapping';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { clearProposalInfo } from 'v3/data/store/proposal/actions';
import { getStorePermissions } from 'v3/data/store/stores/actions';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { ContractInformation } from 'v3/pages/Contracting/Details/Cards/ContractInformation';
import { PersonalInformation } from 'v3/pages/Contracting/Details/Cards/PersonalInformation';
import { ProposalInformation } from 'v3/pages/Contracting/Details/Cards/ProposalInformation';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import { GO_BACK, RoutesEnum } from 'v3/routes/enum';
import * as api from 'v3/services/api';
import { CircularButton } from 'v3/shared/components/Button/CircularButton';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { MiniButton } from 'v3/shared/components/Button/MiniButton';
import { Status } from 'v3/shared/components/Status';
import { CommonTemplate } from 'v3/shared/templates/CommonTemplate';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';
import { AlignMobileButton, AlignStatusAndProof, Container, Content, Header, MainTitle } from './styles';
export const ContractingDetailsPage = () => {
  const navigate = useNavigate();
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const { contractingState } = useStoreSelector((store) => store.contracting);
  const voucher = useStoreSelector((store) => store.voucher);
  const contractingStateTitle = ContractingStateMapping[contractingState]?.title;
  const { contractingReadOnly } = getStorePermissions();
  const { isDesktop, isTablet, isMobile } = useBreakpoints();

  const receiptData = {
    proposal: useStoreSelector((store) => store.proposal),
    person: useStoreSelector((store) => store.person),
    contracting: useStoreSelector((store) => store.contracting.selectedContracting),
    product: useStoreSelector((store) => store.proposal.product),
    device: useStoreSelector((store) => store.device),
    seller: useStoreSelector((store) => store.seller),
  };

  const handleNavigation = () => {
    navigate(GO_BACK);
  };

  const handleSaveLocalStorageData = () => {
    saveLocalStorageData(RECEIPT, receiptData);
  };

  const resetContractingFields = () => {
    setTokenValid(false);
    setTimeRemaining('');
    setContractSignDate('');
  };

  const handleUpdateStatus = useCallback(async () => {
    try {
      const response = await api.getContractingState(contractingKey);
      setContractingState(response.data.status);
      setTokenValid(response.data.tokenValid);
      setTimeRemaining(response.data.timeRemaining);
      setContractSignDate(response.data.contractSignDate);
    } catch (error) {
      if (isAxiosError(error)) {
        resetContractingFields();

        if (error.response?.status === HttpStatusCode.Locked) {
          setContractingState(ContractingStateEnum.CreditAnalysisRejected);
        }
        if (error.response?.status === HttpStatusCode.Gone) {
          setContractingState(ContractingStateEnum.ExpiredKey);
        }
      } else {
        showErrorDialog('Erro ao obter status da contratação', error);
        console.error(error);
      }
    }
  }, []);

  const handleGetContracting = useCallback(async () => {
    try {
      const contracting = await api.getContracting(contractingKey);
      setContractingSelected(contracting.data);
    } catch (error) {
      showErrorDialog('Erro ao obter contratação', error);
    }
  }, []);

  useEffect(() => {
    handleGetContracting();
    clearProposalInfo();
    handleUpdateStatus();
    setContractingKey(contractingKey);
  }, [handleUpdateStatus]);

  const renderProofButton = () => {
    if (contractingState !== ContractingStateEnum.ContractingFinished) return null;

    switch (true) {
      case isDesktop:
        return <FlexButton text="Comprovante" size="small" startIcon={LocalPrintshopOutlinedIcon} />;
      case isTablet:
        return <MiniButton icon={LocalPrintshopOutlinedIcon} />;
      case isMobile:
        return <CircularButton startIcon={LocalPrintshopOutlinedIcon} />;
      default:
        return null;
    }
  };

  return (
    <CommonTemplate>
      <Container>
        <Content>
          <Header>
            <MainTitle>
              <ArrowBackIcon className="pointer" fontSize="large" onClick={handleNavigation} />
              <HeadlineMedium marginRight="auto">Detalhes da proposta</HeadlineMedium>
            </MainTitle>
            <AlignStatusAndProof>
              {isMobile ? (
                <AlignMobileButton>
                  <Fragment>
                    <Link
                      to={`/${RoutesEnum.Receipt}/${contractingKey}`}
                      target="_blank"
                      onClick={handleSaveLocalStorageData}
                    >
                      {renderProofButton()}
                    </Link>

                    <CircularButton startIcon={refreshIcon} handleClick={handleUpdateStatus} />
                  </Fragment>
                </AlignMobileButton>
              ) : (
                <Fragment>
                  <Link
                    to={`/${RoutesEnum.Receipt}/${contractingKey}`}
                    target="_blank"
                    onClick={handleSaveLocalStorageData}
                  >
                    {renderProofButton()}
                  </Link>

                  <FlexButton
                    size="small"
                    text="Atualizar status"
                    endIcon={refreshIcon}
                    handleClick={handleUpdateStatus}
                  />
                </Fragment>
              )}
              <Status title={contractingStateTitle} />
            </AlignStatusAndProof>
          </Header>
          <PersonalInformation />
          <ProposalInformation />
          <ContractInformation readOnly={contractingReadOnly} />
        </Content>
      </Container>
    </CommonTemplate>
  );
};
